import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { MultiSelect } from "../ui/multi-select";
import { toast } from "sonner";
import { fetchUsers } from "../../services/crmServices/userService.js";
import { createDesk, updateDesk } from "../../services/crmServices/deskService.js";
import { Select } from "../ui/select";
import { getFullName, updateFirstLetterToUpperCase } from "../../services/common/util.js";
import { useAuth } from "../../hooks/useAuth";
import { floors } from "../../services/common/constants.js";
import { set } from "date-fns";

export function CreateDeskModal({ open, onClose, onSuccess, desk }) {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        name: "",
        agents: [],
        manager: null,
        floorManager: null,
        floor: ""
    });

    const [allAgents, setAllAgents] = useState([]);
    const [availableAgents, setAvailableAgents] = useState([]);
    const [allManagers, setAllManagers] = useState([]);
    const [availableManagers, setAvailableManagers] = useState([]);
    const [allFloorManagers, setAllFloorManagers] = useState([]);
    const [availableFloorManagers, setAvailableFloorManagers] = useState([]);

    useEffect(() => {
        if (open) {
            if (desk) {
                setFormData({
                    name: desk.name || "",
                    agents: desk.agents.map((item) => item._id) || [],
                    manager: desk.manager?._id,
                    floorManager: desk.floorManager?._id,
                    floor: desk.floor || ""
                });
            }

            fetchUsers().then((res) => {
                if (res?.data?.length) {
                    if (desk) {
                        const agentsTemp = res.data.filter(
                            (user) =>
                                user.position.role === "agent" &&
                                user.position.floor === desk.floor &&
                                (!user.agentDesk || user.agentDesk?._id == desk._id)
                        );
                        setAvailableAgents(agentsTemp);
                        const allAgentsTemp = res.data.filter(
                            (user) =>
                                user.position.role === "agent" && (!user.agentDesk || user.agentDesk?._id == desk._id)
                        );
                        setAllAgents(allAgentsTemp);
                        setAvailableManagers(
                            res.data.filter(
                                (user) => user.position.role === "deskManager" && user.position.floor === desk.floor
                            )
                        );
                        setAvailableFloorManagers(
                            res.data.filter(
                                (user) => user.position.role === "floorManager" && user.position.floor === desk.floor
                            )
                        );
                    } else {
                        const allAgentsTemp = res.data.filter(
                            (user) => user.position.role === "agent" && !user.agentDesk
                        );
                        setAllAgents(allAgentsTemp);
                    }

                    setAllManagers(res.data.filter((user) => user.position.role === "deskManager"));
                    setAllFloorManagers(res.data.filter((user) => user.position.role === "floorManager"));
                }
            });
        }
    }, [open, desk]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.name) {
            toast.error("Please enter a team name");
            return;
        }

        if (!formData.floor) {
            toast.error("Please select a floor");
            return;
        }

        if (desk) {
            updateDesk({ ...formData, _id: desk._id }).then((response) => {
                if (response?.status === 200) {
                    toast.success("Team updated successfully!");
                    setFormData({ name: "", agents: [], manager: null, floorManager: null, floor: "" });
                    onSuccess();
                    onClose();
                } else {
                    if (response.data?.RESULT === "ERROR") {
                        toast.error(response.data.message);
                    } else {
                        toast.error(response.message || "Failed to update team");
                    }
                }
            });
        } else {
            createDesk(formData).then((response) => {
                if (response?.status === 200) {
                    toast.success("Team created successfully!");
                    setFormData({ name: "", agents: [], manager: null, floorManager: null, floor: "" });
                    onSuccess();
                    onClose();
                } else {
                    if (response.data?.RESULT === "ERROR") {
                        toast.error(response.data.message);
                    } else {
                        toast.error(response.message || "Failed to create team");
                    }
                }
            });
        }
    };

    const handleFloorManagerChange = (value) => {
        if (value !== formData.floorManager) {
            setFormData((prev) => ({ ...prev, floorManager: value }));
        }
    };

    const handleManagerChange = (value) => {
        if (value !== formData.manager) {
            setFormData((prev) => ({ ...prev, manager: value }));
        }
    };

    const handleFloorChange = (value) => {
        if (value !== formData.floor) {
            const filteredManagers = allManagers.filter((manager) => manager.position.floor === value);
            const filteredFloorManagers = allFloorManagers.filter(
                (floorManager) => floorManager.position.floor === value
            );
            const filteredAgents = allAgents.filter((agent) => agent.position.floor === value);
            setAvailableManagers(filteredManagers);
            setAvailableFloorManagers(filteredFloorManagers);
            setAvailableAgents(filteredAgents);
            setFormData((prev) => ({ ...prev, floor: value, manager: null, floorManager: null, agents: [] }));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader className="border-b pb-4">
                    <DialogTitle className="text-2xl font-bold text-gray-900">
                        {desk ? "Edit" : "Create New"} Team
                    </DialogTitle>
                    {!desk && (
                        <DialogDescription className="text-gray-600">
                            Set up a new team and assign team members to optimize your workflow
                        </DialogDescription>
                    )}
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-5 pt-2">
                    {/* Team Name */}
                    <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-900 font-semibold">
                            Team Name *
                        </Label>
                        <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Sales Team A"
                            required
                        />
                    </div>

                    {/* Floor Selection */}
                    <div className="space-y-2">
                        <Label htmlFor="floor" className="text-gray-900 font-semibold">
                            Floor *
                        </Label>
                        <Select
                            options={floors.map((floor) => ({
                                label: floor.label,
                                value: floor.value
                            }))}
                            value={formData.floor}
                            onChange={(value) => handleFloorChange(value)}
                            placeholder="Select Floor"
                            searchable
                            disabled={user.position.role !== "admin"}
                        />
                    </div>

                    {/* Management Section */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-sm space-y-4">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                            <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            Management Assignment
                        </h3>
                        
                        <div className="space-y-2">
                            <Label htmlFor="floorManager" className="text-gray-700">
                                Floor Manager
                            </Label>
                            <Select
                                options={availableFloorManagers.map((floorManager) => ({
                                    label: `${getFullName(floorManager)} (${
                                        floorManager.position.floor
                                            ? updateFirstLetterToUpperCase(floorManager.position.floor) + " Floor Manager"
                                            : "N/A"
                                    })`,
                                    value: floorManager._id
                                }))}
                                value={formData.floorManager || null}
                                onChange={(value) => handleFloorManagerChange(value)}
                                placeholder="Select Floor Manager"
                                searchable
                                disabled={user.position.role !== "admin" || !formData.floor}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="desk" className="text-gray-700">
                                Team Manager
                            </Label>
                            <Select
                                options={availableManagers.map((manager) => ({
                                    label: `${getFullName(manager)} (${
                                        manager.position.floor
                                            ? updateFirstLetterToUpperCase(manager.position.floor) + " Manager"
                                            : "N/A"
                                    })`,
                                    value: manager._id
                                }))}
                                value={formData.manager || null}
                                onChange={(value) => handleManagerChange(value)}
                                placeholder="Select Manager"
                                searchable
                                disabled={!/admin|floorManager/.test(user.position.role) || !formData.floor}
                            />
                        </div>
                    </div>

                    {/* Agents Assignment */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-sm space-y-3">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                            <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Team Members
                        </h3>
                        <MultiSelect
                            key={open ? "open" : "closed"}
                            options={availableAgents.map((agent) => ({
                                label: `${getFullName(agent)} (${
                                    agent.position.floor ? updateFirstLetterToUpperCase(agent.position.floor) : "N/A"
                                })`,
                                value: agent._id
                            }))}
                            selected={formData.agents}
                            onChange={(selected) => setFormData((prev) => ({ ...prev, agents: selected }))}
                            placeholder="Select agents"
                            searchPlaceholder="Search agents..."
                            disabled={!formData.manager}
                        />
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">
                                {!formData.manager && "Please select a manager first"}
                            </span>
                            <span className="text-gray-700 font-medium">
                                Selected: {formData.agents.length} agents
                            </span>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4 border-t border-gray-200">
                        <Button 
                            type="submit" 
                            className="flex-1 bg-blue-600 hover:bg-blue-700:bg-blue-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {desk ? "Update Team" : "Create Team"}
                        </Button>
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={onClose}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
